import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { AUTH_URL } from 'src/configs';
import request from 'src/request';

interface OrdersState {
  data: {
    orders: object[];
    totalCount: number;
    totalPages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: OrdersState = {
  data: {
    orders: [],
    totalCount: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
};

// The list endpoint takes status, direction, pair and a created_at range as
// independent filters, so they combine. `kind` has no dedicated parameter but is
// reachable through the generic field/value search, which is merged into the
// same filter — so it composes with the rest.
export const fetchOrders = createAsyncThunk(
  'components/fetchOrders',
  async ({
    page,
    status,
    direction,
    kind,
    pair,
    from,
    to,
    sort,
    order,
  }: {
    page: number;
    status?: string;
    direction?: string;
    kind?: string;
    pair?: string;
    from?: string;
    to?: string;
    sort?: string;
    order?: string;
  }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '10',
    });

    if (status) params.set('status', status);
    if (direction) params.set('direction', direction);
    if (pair) params.set('pair', pair);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (sort) params.set('sort', sort);
    if (order) params.set('order', order);

    if (kind) {
      params.set('field', 'kind');
      params.set('value', kind);
    }

    const response = await request.get(`${AUTH_URL}/v1/admin/orders?${params.toString()}`);
    return {
      data: response.data.orders,
      totalCount: response.data.meta.total_count,
      totalPages: response.data.meta.page_count,
      page,
    };
  }
);

const ordersData = createSlice({
  name: 'orders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.data = {
          orders: action.payload.data,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages,
        };
        state.loading = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch orders';
      });
  },
});

export default ordersData.reducer;
